import {
  belongsOnRoad,
  isCounterCollection,
  startOfDay,
  sumCents,
  tt,
  type FilterChannel,
} from '@agos/shared'

// §9.343 - the settling-status rule moved to `@agos/shared` when a third caller needed it. Re-exported
// so everything that already imported it from here is unaffected by where it lives.
export {
  SETTLING_STATUSES,
  credentialsNeeded,
  protectedStatusPlan,
  statusRoleNote,
} from '@agos/shared'

import { isOnTheBoard } from './dispatch-rules'

/**
 * Which orders the dispatch board shows, in what order, and what it says about
 * the drivers currently out.
 *
 * Ported from legacy `pages/dispatch.tsx`.
 */

export interface BoardOrder {
  id?: number
  orderNumber?: string
  type?: string
  status?: string
  createdAt?: string
  scheduledAt?: string
  driverId?: number | null
  driverName?: string
  /** The waiter who took it. Null on a bar bill nobody attributed — FB-075's gap. */
  employeeId?: number | null
  total?: number | string
  /**
   * What the customer wrote — §9.54.
   *
   * `/orders` has always carried it and this type never named it, so the board could not draw it.
   * Nobody typing a note knows who it is for: *zvoňte na susedov* is for the driver this screen
   * assigns.
   */
  notes?: string | null
  /**
   * Who the bag is for, and how much to trust the order — §9.125, the owner's point 11.
   *
   * `orderFull` has always carried every one of these and this type named none of them, so the board
   * could not draw what it was already being sent. The same omission as `notes` above, found the same
   * way: the owner asked where the information went.
   */
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string | null
  /** `web` for an order nobody spoke to, `phone` for one somebody took. */
  source?: string
  paymentStatus?: string
  /**
   * How it will be paid, and what was taken off — §9.154.
   *
   * <p>Both have been on the `/orders` payload since the beginning and this type named neither, so
   * the board drew a total with no idea whether the money had arrived, was coming with the courier,
   * or had been discounted on the way. The same omission as `notes` and the customer block above.
   */
  paymentMethod?: string
  discount?: number | string
  /** A pre-order has a promised time; §9.154 draws the countdown to it. */
  isPreorder?: boolean
  isEdited?: boolean
  /** How many orders this number has placed. Used from **two** upward — see `verificationOf`. */
  phoneOrderCount?: number
  emailOrderCount?: number
  /**
   * Where it is going, and which zone the server put it in - §9.351, §10A.139.
   *
   * `deliveryLat` has been on the `/orders` payload since the beginning and this type never named it;
   * `deliveryZoneName` is new, because until §10A.139 the server resolved the zone and threw the
   * answer away. **Both are needed together** and neither means anything alone - see
   * {@link outsideEveryZone}.
   */
  deliveryLat?: number | null
  deliveryZoneName?: string | null
  /**
   * Whether the server looked for a zone at all - `V55`, correcting §10A.139.
   *
   * **This is what makes a null name readable.** `V54` back-fills nothing, so every order written
   * before it has coordinates and no zone name - and the first version of {@link outsideEveryZone}
   * would have badged all 6 070 of the owner's geocoded orders. Measured, only **3** of them are in
   * a status the board shows, so the effect was bounded to orders in flight at deployment - but a
   * badge that says *mimo zóny* about an order inside a zone is a screen stating a falsehood, and
   * presenting it as normal is the failure this repository keeps finding.
   */
  deliveryZoneChecked?: boolean
}

/**
 * Whether the server looked for a delivery zone and found none - §9.351.
 *
 * **The flag is a pair of columns, not a field**, and that is deliberate (§10A.139): a null
 * `deliveryZoneName` means two different things depending on whether the order has coordinates.
 *
 * | checked | coordinates | zone name | what it is |
 * |---|---|---|---|
 * | yes | present | a name | inside that zone |
 * | **yes** | **present** | **null** | **the server looked and found none** - 147 of the owner's orders |
 * | yes | null | null | no coordinates, so no rule could apply - 1 248 of them |
 * | **no** | anything | anything | **written before the recording existed** - every historical order |
 *
 * **Badging the third case would bury the second.** A phone order that somebody typed an address
 * into has no coordinates and therefore no zone by construction; there are 1 248 of those against
 * 147 that mean something. So the rule is *coordinates and no zone*, written once here rather than
 * spelled at each call site - which is how §10A.136's one-reader-spelled-twice happened.
 *
 * Nothing about this refuses or blocks: §10A.139 accepts every one of these, and the owner's
 * *"we can loose a lot of money"* is why. It is a marker for whoever is assigning a driver.
 */
export function outsideEveryZone(order: BoardOrder): boolean {
  if (order.deliveryZoneChecked !== true) return false
  const hasCoordinates = order.deliveryLat !== null && order.deliveryLat !== undefined
  const named = (order.deliveryZoneName ?? '').trim()
  return hasCoordinates && named === ''
}

/**
 * Whether a pre-order's day has arrived.
 *
 * Scheduled *today* or already overdue is today's work; anything further out is not. Compared by
 * local day, not by instant — a delivery booked for 19:00 tonight is today's whatever the clock says
 * now.
 *
 * **This used to decide whether the row was on the board at all, and §9.318 makes it decide which
 * band it goes in** (owner Q8: *"Predobjednávky - not showing in kuchyna, rozvoz - nástenka etc."*).
 * The old comment said a later-day pre-order *"lives on the pre-order screen"* while the kitchen's
 * said it *"belongs on the dispatch board until it comes due"* - each screen handed it to the other
 * and it appeared on neither.
 */
export function isDueToday(order: BoardOrder, now: Date): boolean {
  if (!order.scheduledAt) return true
  return startOfDay(new Date(order.scheduledAt)).getTime() <= startOfDay(now).getTime()
}

/**
 * A pre-order whose day has not come — §9.318.
 *
 * `isPreorder` is **not** required: the flag and `scheduledAt` have disagreed on real rows (§10A.88's
 * neighbourhood), and a scheduled time in a later day is the thing that makes it not-yet-due whatever
 * the flag says. A row with no `scheduledAt` can never be one.
 */
export function isLaterDayPreorder(order: BoardOrder, now: Date): boolean {
  return Boolean(order.scheduledAt) && !isDueToday(order, now)
}

/**
 * The board is the road, so table orders are not on it — the restaurant screen
 * owns those.
 *
 * **§9.5.** This was `order.type !== 'restaurant'`, which stopped being the same
 * statement the moment §10A.1 let a franchise add a second table-serving
 * channel. `na_sekeru` is exactly that channel, and it would have appeared on
 * the road. `belongsOnRoad` asks the capability — `usesTables` — instead.
 *
 * The channels are a **required** argument rather than an optional one with a
 * fallback: a fallback would silently reproduce the bug for any caller that
 * forgot, and forgetting is the whole failure mode. The page waits for them
 * before drawing the board, so a table order never flashes onto the road.
 */
export function boardOrders(
  orders: BoardOrder[],
  channels: readonly FilterChannel[],
): BoardOrder[] {
  // §9.318 - membership is the channel alone now. `isDueToday` used to be the second half of this
  // test; a later-day pre-order stays on the board and `boardBands` gives it a band of its own,
  // because an order the restaurant has taken must not be invisible on the screen the dispatcher
  // watches. **The `now` parameter went with the rule** - a parameter nothing reads is a caller
  // being told this still depends on the clock when it does not.
  return orders.filter((order) => belongsOnRoad(order.type ?? '', channels))
}

export function activeOrders(orders: BoardOrder[]): BoardOrder[] {
  return orders.filter((order) => isOnTheBoard(order.status))
}

/**
 * Counter collections first, then oldest.
 *
 * Somebody is standing at the counter waiting for a collection; a delivery has a
 * driver's journey still to absorb the wait. Legacy sorts this way and the reason
 * is the same one the kitchen puts seated guests before deliveries.
 */
export function sortForBoard(
  orders: BoardOrder[],
  channels: readonly FilterChannel[],
): BoardOrder[] {
  return [...orders].sort((left, right) => {
    // §9.5: on the premises but not at a table, rather than the literal `bar`.
    const byCollection = (order: BoardOrder) =>
      isCounterCollection(order.type ?? '', channels) ? 0 : 1
    if (byCollection(left) !== byCollection(right)) {
      return byCollection(left) - byCollection(right)
    }
    return new Date(left.createdAt ?? 0).getTime() - new Date(right.createdAt ?? 0).getTime()
  })
}

/**
 * The board's tabs: `all`, `active`, or one exact status.
 *
 * `all` is the owner's request — *"nejde si zobraziť všetko"*. It is genuinely everything the board
 * holds rather than a wider status list: `boardOrders` has already scoped to today's road orders, so
 * *all* means every status within that, delivered and cancelled included. Without it there was no way
 * to see an order once it left `active`, and the seven status tabs are only useful if you already know
 * which one it landed in.
 *
 * `active` stays the **default**. It is the working view — what still needs doing — and opening onto a
 * list padded with this morning's delivered orders would cost the dispatcher the thing they came for.
 */
export function visibleOrders(
  orders: BoardOrder[],
  filterStatus: string,
  channels: readonly FilterChannel[],
): BoardOrder[] {
  // §9.318 - `now` went with `boardOrders`'. What the clock decides is which **band** a row lands in,
  // and that is `boardBands`' argument now.
  const onBoard = boardOrders(orders, channels)
  const filtered =
    filterStatus === 'all'
      ? onBoard
      : filterStatus === 'active'
        ? activeOrders(onBoard)
        : onBoard.filter((order) => order.status === filterStatus)
  return sortForBoard(filtered, channels)
}

/**
 * The bands the board is drawn in, in road order — §9.104.
 *
 * <p>Waiting to be accepted, accepted, being cooked, cooked, checked, gone, arrived. `pending` leads
 * because an unaccepted order is the one thing on the board nobody has looked at yet; the status enum's
 * own order is alphabetical and means nothing to a dispatcher.
 *
 * <p>Matches legacy `dispatch.tsx:1693` band for band, which is what the owner asked for: *"we need
 * that rozvoz - nástenka to be kinda the same way"*.
 */
export const BOARD_BANDS = [
  { status: 'pending', label: tt('Čakajú na prijatie'), dot: 'bg-red-500' },
  { status: 'new', label: tt('Nové objednávky'), dot: 'bg-green-500' },
  { status: 'preparing', label: tt('V príprave'), dot: 'bg-violet-500' },
  { status: 'ready', label: tt('Pripravené'), dot: 'bg-amber-500' },
  { status: 'quality_check', label: tt('Kontrola kvality'), dot: 'bg-blue-500' },
  { status: 'on_the_way', label: tt('Na ceste'), dot: 'bg-zinc-500' },
  { status: 'at_address', label: tt('Na adrese'), dot: 'bg-orange-500' },
] as const

export interface BoardBand {
  status: string
  label: string
  dot: string
  orders: BoardOrder[]
}

/**
 * The board's orders split into their bands.
 *
 * <p><b>Empty bands are dropped</b>, as legacy drops them — seven headers over an empty evening is
 * six lines of furniture between the dispatcher and the one order that matters.
 *
 * <p><b>Anything in a status no band names is kept, in a band of its own at the end.</b> Silently
 * dropping it would take an order off the board because a status was added to the backend and not
 * here — the board's whole job is that nothing on it is invisible.
 */
export function boardBands(orders: BoardOrder[], now: Date = new Date()): BoardBand[] {
  // §9.318 - a pre-order for a later day goes in its own band at the end, not in the band of whatever
  // status it happens to carry. `new` and `pending` are the statuses these arrive in, and mixing
  // tomorrow's order into *Nové objednávky* is how a cook starts it tonight.
  const queued = orders.filter((order) => isLaterDayPreorder(order, now))
  const queuedIds = new Set(queued.map((order) => order.id))
  const today = orders.filter((order) => !queuedIds.has(order.id))

  const bands: BoardBand[] = BOARD_BANDS.map((band) => ({
    ...band,
    orders: today.filter((order) => order.status === band.status),
  })).filter((band) => band.orders.length > 0)

  const named = new Set<string>(BOARD_BANDS.map((band) => band.status))
  const rest = today.filter((order) => !named.has(String(order.status)))
  if (rest.length > 0) {
    bands.push({ status: 'other', label: tt('Ostatné'), dot: 'bg-muted-foreground', orders: rest })
  }

  if (queued.length > 0) {
    bands.push({
      status: PREORDER_BAND,
      label: tt('Predobjednávky na ďalšie dni'),
      dot: 'bg-amber-400',
      // Soonest first: the dispatcher reads this band to know what tomorrow looks like.
      orders: [...queued].sort(
        (left, right) =>
          new Date(left.scheduledAt ?? 0).getTime() - new Date(right.scheduledAt ?? 0).getTime(),
      ),
    })
  }
  return bands
}

/** The band a later-day pre-order sits in. Named, because the page styles it differently. */
export const PREORDER_BAND = 'preorder_queue'

export interface DriverLoad {
  driverId: number
  name: string
  orderCount: number
  /** In cents — §14.7. */
  totalCents: number
}

const OUT_ON_THE_ROAD = ['on_the_way', 'at_address']

/**
 * Who is out, with how many bags and how much money.
 *
 * FB-058: legacy sums `o.total ?? 0` with `+`, and `total` arrives as a `numeric`
 * string, so the figure concatenates into nonsense as soon as a driver has two
 * orders. Assembled with `sumCents` here.
 *
 * Busiest driver first — the dispatcher's next decision is who *not* to give the
 * next order to.
 */
export function driverLoads(orders: BoardOrder[]): DriverLoad[] {
  const byDriver = new Map<number, { name: string; totals: Array<number | string | undefined> }>()

  for (const order of orders) {
    if (!OUT_ON_THE_ROAD.includes(order.status ?? '') || !order.driverId) continue
    const driverId = Number(order.driverId)
    const existing = byDriver.get(driverId)
    if (existing) {
      existing.totals.push(order.total)
    } else {
      byDriver.set(driverId, {
        name: order.driverName || `${tt('Šofér')} #${driverId}`,
        totals: [order.total],
      })
    }
  }

  return [...byDriver.entries()]
    .map(([driverId, driver]) => ({
      driverId,
      name: driver.name,
      orderCount: driver.totals.length,
      totalCents: sumCents(...driver.totals),
    }))
    .sort((left, right) => right.orderCount - left.orderCount)
}

export interface ArrivalNotice {
  /** Taken on the web and not yet accepted — the urgent ones. */
  pending: BoardOrder[]
  /** Accepted and now in the process. */
  accepted: BoardOrder[]
}

/**
 * Which orders are new since the last look.
 *
 * `seen` being `null` means this is the first load, and the first load announces
 * nothing — a dispatcher opening the board to nine waiting orders does not need
 * to be told nine orders arrived. Every later comparison does announce, including
 * from an empty board, which is the case the kitchen's version got wrong
 * (FB-047).
 */
export function arrivalsSince(
  orders: BoardOrder[],
  seen: ReadonlySet<number> | null,
): ArrivalNotice {
  if (seen === null) return { pending: [], accepted: [] }

  const fresh = orders.filter(
    (order) =>
      (order.status === 'new' || order.status === 'pending') && !seen.has(order.id ?? 0),
  )

  return {
    pending: fresh.filter((order) => order.status === 'pending'),
    accepted: fresh.filter((order) => order.status === 'new'),
  }
}

export function orderIdsOf(orders: BoardOrder[]): Set<number> {
  return new Set(orders.map((order) => order.id ?? 0))
}

/**
 * What the settling dialog says it is about.
 *
 * Names the **status** as well as the order, because the same dialog now handles all three: a
 * heading reading *Stornovať objednávku* over a close would be the dialog lying about what the
 * button does.
 */
export function statusChangeSubtitle(order: BoardOrder, status: string): string {
  const who = order.customerName ?? `#${order.orderNumber ?? order.id ?? ''}`
  if (status === 'cancelled') return `${tt('Storno')}: ${who}`
  if (status === 'quality_check') return `${tt('Vrátiť do kuchyne')}: ${who}`
  return `${tt('Uzavrieť')}: ${who}`
}

/**
 * The dialog's heading - §9.409.
 *
 * **The comment above was right and the subtitle was not enough.** The heading and the confirm
 * button are the two things somebody reads before pressing, and both were hard-coded to the cancel
 * wording, so pressing *Vydaná* opened a dialog headed *Stornovať objednávku* over a subtitle
 * reading *Uzavrieť*. The owner: *"we will see 'Stornovať objednávku' when clicking vydané."*
 *
 * **The wording follows the subtitle rather than the button on the card.** `Vydaná` and `Doručená`
 * are two buttons for one status - in-house and delivery - so a heading naming either would be
 * wrong half the time. `Uzavrieť` already covers both, which is why `statusChangeSubtitle` chose it.
 */
export function statusChangeTitle(status: string): string {
  if (status === 'cancelled') return tt('Stornovať objednávku')
  if (status === 'quality_check') return tt('Vrátiť do kuchyne')
  return tt('Uzavrieť objednávku')
}

/**
 * The label on the button that commits it - §9.409.
 *
 * Beside the heading rather than derived from it, and decided by the same `status`: a button reading
 * *Stornovať* under a heading reading *Uzavrieť objednávku* is the same defect one layer down.
 */
export function statusChangeConfirmLabel(status: string): string {
  if (status === 'cancelled') return tt('Stornovať')
  if (status === 'quality_check') return tt('Vrátiť')
  return tt('Uzavrieť')
}
